import { useEffect } from 'react';

import { useHotkeysContext } from 'react-hotkeys-hook';

/**
 * `react-hotkeys-hook` scope name for all ribbon settings shortcuts
 * (§5.1 of docs/keyboard-shortcuts-proposal.md).
 */
export const RIBBON_SCOPE = 'ribbon';

/**
 * Enables/disables the `ribbon` hotkeys scope based on whether something
 * else currently has the user's attention - a modal dialog, or a side/
 * review panel sliding in over the map. While `blocked` is true, none of
 * the ribbon shortcuts registered under `RIBBON_SCOPE` will fire.
 *
 * This closes the gap described in §2.4 of the proposal: ribbon shortcuts
 * previously had no way to know about modal/panel openness at all.
 *
 * Must be called from a component rendered *inside* `HotkeysProvider` -
 * `useHotkeysContext()` reads that provider's React context.
 *
 * @param blocked true when a modal or side/review panel is open
 */
export function useRibbonHotkeysScope(blocked: boolean) {
  const { enableScope, disableScope } = useHotkeysContext();

  useEffect(() => {
    if (blocked) {
      disableScope(RIBBON_SCOPE);
    } else {
      enableScope(RIBBON_SCOPE);
    }
  }, [blocked, enableScope, disableScope]);
}

/**
 * Renderless component wrapper around `useRibbonHotkeysScope`, so the
 * scope-sync effect can be mounted directly in JSX alongside the rest of
 * the drawing editor tree, right under `HotkeysProvider`.
 */
export const RibbonHotkeysScopeSync = ({ blocked }: { blocked: boolean }) => {
  useRibbonHotkeysScope(blocked);
  return null;
};
